import { ProductType as CartProductType } from '@/api/Cart/cart.interfaces';
import { ProductType as BookingProductType } from '@/api/MyBooking/myBooking.interfaces';
import { isNotNil } from '@shared/utils/value';
import {
    CartProductCombination,
    ProductCombinationType,
    BookingProductCombination,
    BookingProductCombinationIncludes,
    CartProductCombinationIncludes,
} from '@shared/interfaces/productCombination';

enum Type {
    Hotel = 'Hotel',
    Flight = 'Flight',
    Ferry = 'Ferry',
    Activity = 'Activity',
}

export function getCartProductCombination(
    types: CartProductType[]
): CartProductCombination | null {
    return buildProductCombination(types, 'Cart') as CartProductCombination;
}

export function getBookingProductCombination(
    types: BookingProductType[]
): BookingProductCombination | null {
    return buildProductCombination(types, 'Booking') as BookingProductCombination;
}

function buildProductCombination(
    types: (CartProductType | BookingProductType)[],
    dataType: 'Cart' | 'Booking'
): BookingProductCombination | CartProductCombination | null {
    const unifiedTypes = types.map(toUnifiedProductType).filter(isNotNil);
    const includes: BookingProductCombinationIncludes =
        getProductCombinationIncludes(unifiedTypes);

    if (dataType === 'Cart') {
        const cartIncludes: CartProductCombinationIncludes = {
            Flight: includes.flight,
            Ferry: includes.ferry,
            Hotel: includes.hotel,
            Activity: includes.activity,
        };

        return {
            Type: getProductCombinationType(includes),
            Includes: cartIncludes,
        };
    } else {
        return {
            type: getProductCombinationType(includes),
            includes,
        };
    }
}

function toUnifiedProductType(type: CartProductType | BookingProductType): Type | null {
    switch (type) {
        case CartProductType.Hotel:
        case BookingProductType.Hotel:
            return Type.Hotel;

        case CartProductType.Flight:
        case BookingProductType.Flight:
            return Type.Flight;

        case CartProductType.Ferry:
        case BookingProductType.Ferry:
            return Type.Ferry;

        case CartProductType.Activity:
        case BookingProductType.Activity:
            return Type.Activity;

        default:
            return null;
    }
}

function getProductCombinationIncludes(types: Type[]): BookingProductCombinationIncludes {
    return {
        flight: hasFlightProduct(types),
        ferry: hasFerryProduct(types),
        hotel: hasHotelProduct(types),
        activity: hasActivityProduct(types),
    };
}

function getProductCombinationType(
    includes: BookingProductCombinationIncludes
): ProductCombinationType {
    // 1 product
    if (isHotelOnly(includes)) {
        return ProductCombinationType.Hotel;
    }

    if (isFlightOnly(includes)) {
        return ProductCombinationType.Flight;
    }

    if (isFerryOnly(includes)) {
        return ProductCombinationType.Ferry;
    }

    if (isActivityOnly(includes)) {
        return ProductCombinationType.Activity;
    }

    // 2 products
    if (isHotelFlight(includes)) {
        return ProductCombinationType.HotelFlight;
    }

    if (isHotelFerry(includes)) {
        return ProductCombinationType.HotelFerry;
    }

    if (isHotelActivity(includes)) {
        return ProductCombinationType.HotelActivity;
    }

    // 3 products
    if (isHotelFlightFerry(includes)) {
        return ProductCombinationType.HotelFlightFerry;
    }
    if (isHotelFlightActivity(includes)) {
        return ProductCombinationType.HotelFlightActivity;
    }
    if (isHotelFerryActivity(includes)) {
        return ProductCombinationType.HotelFerryActivity;
    }
    if (isFlightFerryActivity(includes)) {
        return ProductCombinationType.FlightFerryActivity;
    }

    // 4 products
    return ProductCombinationType.HotelFlightFerryActivity;
}

function hasType(types: Type[], type: Type): boolean {
    return types.includes(type);
}

function hasHotelProduct(types: Type[]): boolean {
    return hasType(types, Type.Hotel);
}

function hasFlightProduct(types: Type[]): boolean {
    return hasType(types, Type.Flight);
}

function hasFerryProduct(types: Type[]): boolean {
    return hasType(types, Type.Ferry);
}

function hasActivityProduct(types: Type[]): boolean {
    return hasType(types, Type.Activity);
}

function isHotelOnly(includes: BookingProductCombinationIncludes): boolean {
    return includes.hotel && !includes.activity && !includes.ferry && !includes.flight;
}

function isFlightOnly(includes: BookingProductCombinationIncludes): boolean {
    return includes.flight && !includes.hotel && !includes.ferry && !includes.activity;
}

function isFerryOnly(includes: BookingProductCombinationIncludes): boolean {
    return includes.ferry && !includes.hotel && !includes.flight && !includes.activity;
}

function isActivityOnly(includes: BookingProductCombinationIncludes): boolean {
    return includes.activity && !includes.hotel && !includes.ferry && !includes.flight;
}

function isHotelFlight(includes: BookingProductCombinationIncludes): boolean {
    return includes.hotel && includes.flight && !includes.ferry && !includes.activity;
}

function isHotelFerry(includes: BookingProductCombinationIncludes): boolean {
    return includes.hotel && includes.ferry && !includes.flight && !includes.activity;
}

function isHotelActivity(includes: BookingProductCombinationIncludes): boolean {
    return includes.hotel && includes.activity && !includes.flight && !includes.ferry;
}

function isHotelFlightFerry(includes: BookingProductCombinationIncludes): boolean {
    return includes.hotel && includes.flight && includes.ferry && !includes.activity;
}

function isHotelFlightActivity(includes: BookingProductCombinationIncludes): boolean {
    return includes.hotel && includes.flight && includes.activity && !includes.ferry;
}

function isHotelFerryActivity(includes: BookingProductCombinationIncludes): boolean {
    return includes.hotel && includes.ferry && includes.activity && !includes.flight;
}

function isFlightFerryActivity(includes: BookingProductCombinationIncludes): boolean {
    return includes.flight && includes.ferry && includes.activity && !includes.hotel;
}

// function isHotelFlightFerryActivity(
//     includes: BookingProductCombinationIncludes
// ): boolean {
//     return (
//         includes.hotel &&
//         includes.flight &&
//         includes.ferry &&
//         includes.activity
//     );
// }
